import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Download, AlertTriangle, CheckCircle2, ArrowUp } from "lucide-react";
import {
  createResolver,
  resolveGuest,
  buildLine,
  buildFile,
  tiposFor,
  blockingErrors,
  MAX_LINES,
} from "../utils/alloggiati";

// Fichero de Alloggiati Web (Italia) con los DNI y pasaportes seleccionados del
// historial (28-9-2026). Las tablas oficiales (~470 KB) se cargan solo al abrir.

const fieldsOf = (entry) => entry.fields || entry.extractedJson?.fields || [];

function toItalian(d) {
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

export default function AlloggiatiModal({ entries, onClose }) {
  const { t } = useTranslation();
  const [tables, setTables] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [arrival, setArrival] = useState("today");
  const [days, setDays] = useState(1);
  const [mode, setMode] = useState("single");
  const [order, setOrder] = useState(() => entries.map((e) => e.resultId || e.requestId));
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    let alive = true;
    Promise.all([
      import("../data/alloggiati/comuni.json"),
      import("../data/alloggiati/stati.json"),
      import("../data/alloggiati/documenti.json"),
      import("../data/alloggiati/iso.json"),
    ])
      .then(([c, s, d, i]) => {
        if (alive) setTables({ comuni: c.default, stati: s.default, documenti: d.default, iso: i.default });
      })
      .catch(() => alive && setLoadError(true));
    return () => {
      alive = false;
    };
  }, []);

  const resolver = useMemo(() => (tables ? createResolver(tables) : null), [tables]);
  const byId = useMemo(() => new Map(entries.map((e) => [e.resultId || e.requestId, e])), [entries]);
  const tipos = tiposFor(mode, order.length);

  const guests = useMemo(() => {
    if (!resolver) return [];
    return order.map((id, i) => {
      const r = resolveGuest(fieldsOf(byId.get(id)), resolver, overrides[id] || {});
      return { id, tipo: tipos[i], ...r, blocking: blockingErrors(r.errors, tipos[i]) };
    });
  }, [resolver, order, byId, overrides, tipos]);

  const arrivalDate = (() => {
    const d = new Date();
    if (arrival === "yesterday") d.setDate(d.getDate() - 1);
    return toItalian(d);
  })();
  const blocked = guests.some((g) => g.blocking.length > 0);
  const tooMany = guests.length > MAX_LINES;

  const setOverride = (id, key, value) =>
    setOverrides((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: value } }));

  const moveToTop = (id) => setOrder((prev) => [id, ...prev.filter((x) => x !== id)]);

  const download = () => {
    const lines = guests.map((g) => buildLine(g.values, { tipo: g.tipo, arrival: arrivalDate, days }));
    const blob = new Blob([buildFile(lines)], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `alloggiati_${arrivalDate.split("/").reverse().join("")}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const statoName = (code) => (resolver && code ? resolver.stato(code)?.name || code : "");
  const statiOptions = resolver ? resolver.activeStati : [];
  const docOptions = resolver ? resolver.documenti : [];
  const selectCls = "w-full rounded-lg border border-amber-300 bg-white px-2 py-1 text-xs";

  const renderFix = (g, key) => {
    const ov = overrides[g.id] || {};
    if (key === "sesso") {
      return (
        <select className={selectCls} value={ov.sesso || ""} onChange={(e) => setOverride(g.id, "sesso", e.target.value)}>
          <option value="">—</option>
          <option value="M">M</option>
          <option value="F">F</option>
        </select>
      );
    }
    if (key === "cittadinanza" || key === "statoNascita" || key === "luogoRilascio") {
      return (
        <select className={selectCls} value={ov[key] || ""} onChange={(e) => setOverride(g.id, key, e.target.value)}>
          <option value="">{t("alloggiati.choose_country")}</option>
          {statiOptions.map(([code, name]) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </select>
      );
    }
    if (key === "tipoDocumento") {
      return (
        <select className={selectCls} value={ov.tipoDocumento || ""} onChange={(e) => setOverride(g.id, "tipoDocumento", e.target.value)}>
          <option value="">{t("alloggiati.choose_document")}</option>
          {docOptions.map(([code, name]) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </select>
      );
    }
    const placeholder = key === "nascita" ? "gg/mm/aaaa" : key === "comuneNascita" ? t("alloggiati.comune_placeholder") : "";
    return (
      <input
        className={selectCls}
        placeholder={placeholder}
        value={ov[key] || ""}
        onChange={(e) => setOverride(g.id, key, e.target.value)}
      />
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 p-2 sm:p-4 overflow-y-auto">
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl my-4">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 p-4 sm:p-5">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{t("alloggiati.title")}</h3>
            <p className="mt-1 text-sm text-gray-500">{t("alloggiati.subtitle", { count: entries.length })}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label={t("alloggiati.close")}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {loadError && <p className="p-5 text-sm text-red-600">{t("alloggiati.load_error")}</p>}
        {!tables && !loadError && <p className="p-5 text-sm text-gray-500">{t("alloggiati.loading")}</p>}

        {resolver && (
          <div className="p-4 sm:p-5 space-y-5">
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="text-xs font-semibold text-gray-600">
                {t("alloggiati.arrival")}
                <select className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm" value={arrival} onChange={(e) => setArrival(e.target.value)}>
                  <option value="today">{t("alloggiati.today")}</option>
                  <option value="yesterday">{t("alloggiati.yesterday")}</option>
                </select>
                <span className="mt-1 block font-normal text-gray-400">{t("alloggiati.arrival_rule")}</span>
              </label>
              <label className="text-xs font-semibold text-gray-600">
                {t("alloggiati.nights")}
                <input
                  type="number"
                  min={1}
                  max={30}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                  value={days}
                  onChange={(e) => setDays(Math.max(1, Math.min(30, Number(e.target.value) || 1)))}
                />
              </label>
              <label className="text-xs font-semibold text-gray-600">
                {t("alloggiati.guests_are")}
                <select className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm" value={mode} onChange={(e) => setMode(e.target.value)}>
                  <option value="single">{t("alloggiati.mode_single")}</option>
                  <option value="family">{t("alloggiati.mode_family")}</option>
                  <option value="group">{t("alloggiati.mode_group")}</option>
                </select>
              </label>
            </div>

            <ul className="space-y-3">
              {guests.map((g, i) => {
                const v = g.values;
                const isHead = mode !== "single" && i === 0;
                return (
                  <li key={g.id} className={`rounded-xl border p-3 ${g.blocking.length ? "border-amber-300 bg-amber-50/40" : "border-gray-200"}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      {g.blocking.length ? (
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 text-primary-600" />
                      )}
                      <span className="font-semibold text-gray-900">
                        {[v.cognome, v.nome].filter(Boolean).join(" ") || t("alloggiati.no_name")}
                      </span>
                      {mode !== "single" && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-gray-600">
                          {isHead ? t(mode === "family" ? "alloggiati.head_family" : "alloggiati.head_group") : t("alloggiati.member")}
                        </span>
                      )}
                      {mode !== "single" && !isHead && (
                        <button onClick={() => moveToTop(g.id)} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:underline">
                          <ArrowUp className="h-3 w-3" />
                          {t(mode === "family" ? "alloggiati.make_head_family" : "alloggiati.make_head_group")}
                        </button>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {[
                        v.sesso === "1" ? "M" : v.sesso === "2" ? "F" : null,
                        v.nascita,
                        v.comuneNascita ? `${resolver.comuneName(v.comuneNascita)} (${v.provinciaNascita})` : statoName(v.statoNascita),
                        v.cittadinanza ? `${t("alloggiati.citizenship")}: ${statoName(v.cittadinanza)}` : null,
                        g.blocking.includes("tipoDocumento") || !["16", "17", "18"].includes(g.tipo)
                          ? null
                          : `${v.tipoDocumento} ${v.numeroDocumento}`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    {g.warnings.includes("stato_from_citizenship") && !(overrides[g.id] || {}).statoNascita && (
                      <div className="mt-2 grid gap-1 sm:grid-cols-2 sm:items-center">
                        <span className="text-xs text-amber-700">{t("alloggiati.warn_birth_country", { country: statoName(v.statoNascita) })}</span>
                        {renderFix(g, "statoNascita")}
                      </div>
                    )}
                    {g.blocking.length > 0 && (
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        {g.blocking.map((key) => (
                          <label key={key} className="text-xs font-semibold text-amber-800">
                            {t(`alloggiati.field_${key}`)}
                            <div className="mt-1 font-normal">{renderFix(g, key)}</div>
                          </label>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            {tooMany && <p className="text-sm text-red-600">{t("alloggiati.too_many", { max: MAX_LINES })}</p>}

            <div className="flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-gray-500">{t("alloggiati.how_to_upload")}</p>
              <button
                onClick={download}
                disabled={blocked || tooMany || guests.length === 0}
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold transition ${
                  blocked || tooMany ? "cursor-not-allowed bg-gray-200 text-gray-400" : "bg-primary-600 text-white hover:bg-primary-700"
                }`}
              >
                <Download className="h-4 w-4" />
                {blocked ? t("alloggiati.fix_first") : t("alloggiati.download")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
